import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import EntityForm from "../EntityForm/EntityForm";
import "./customStyles.css";
import CancellationPolicyConfigurationList from "./CancellationPolicyConfiguration/CancellationPolicyConfigurationList";
import CancellationPolicyConfigurationForm from "./CancellationPolicyConfiguration/CancellationPolicyConfigurationForm";
import { entityToFormValuesMapper } from "../../Common/utils";
import CONSTANTS from "../constants";
import asBookingEngineEntity from "../Common/BookingEngineEntityHOC";
import { defineMessages, useIntl } from "react-intl";
import { usePropertyConfiguration } from "../../../../components/property-configuration-provider";
import { useChainData } from "../../../../components/chain-data-provider";
import styled from "styled-components";
import DualListBoxGroup from "../Common/DualListBoxGroup";
import { isAgrPolicy, parentTickerOf } from "./domain/agr.helpers";
import { fetchAgrPolicyData, saveAgrPolicy } from "./infrastructure/cancellationPolicyAgr.api";
import { normalizeConfigurations, buildPolicyChainBody, buildAgrReadonlyLink } from "./application/cancellationPolicyForm.helpers";

const AgrWarning = styled.div`
  margin: 8px 0 16px;
  padding: 10px 14px;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  color: #856404;
  font-size: 13px;
`;

const AgrReadonlyBanner = styled.div`
  margin-bottom: 16px;
  padding: 10px 14px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  color: #721c24;
  font-size: 13px;

  a {
    color: #721c24;
    font-weight: 600;
    text-decoration: underline;
    margin-left: 4px;
  }
`;

const AgrError = styled.div`
  margin-bottom: 16px;
  padding: 10px 14px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  color: #721c24;
  font-size: 13px;
`;

const messages = defineMessages({
  bothOrNone: {
    id: "app.cancellationPolicies.bothOrNone",
    defaultMessage: "Both the start date and the end date must be present",
  },
  agrWarningMessage: {
    id: "app.cancellationPolicyForm.agrWarningMessage",
    defaultMessage:
      "Changes to this cancellation policy will be applied to all engines associated with the aggregator.",
  },
  agrReadonlyMessage: {
    id: "app.cancellationPolicyForm.agrReadonlyMessage",
    defaultMessage:
      "This cancellation policy is managed from the aggregator engine. Only automatic charge settings can be independently enabled or disabled on this motor.",
  },
  agrReadonlyEditRest: {
    id: "app.cancellationPolicyForm.agrReadonlyEditRest",
    defaultMessage: "To edit other fields,",
  },
  agrReadonlyLink: {
    id: "app.cancellationPolicyForm.agrReadonlyLink",
    defaultMessage: "click here",
  },
  agrMinOneMotorError: {
    id: "app.cancellationPolicyForm.agrMinOneMotorError",
    defaultMessage: "At least one child motor must be selected.",
  },
  agrSaveError: {
    id: "app.cancellationPolicyForm.agrSaveError",
    defaultMessage: "An error occurred while saving the cancellation policy. Please try again.",
  },
  agrSaveErrorLinkedToConditions: {
    id: "app.cancellationPolicyForm.agrSaveErrorLinkedToConditions",
    defaultMessage: "One or more motors cannot be removed because the policy is linked to conditions in those motors.",
  },
  agrSaveErrorLinkedToConditionMotors: {
    id: "app.cancellationPolicyForm.agrSaveErrorLinkedToConditionMotors",
    defaultMessage:
      "This cancellation policy cannot be unlinked from {count, plural, one {engine} other {engines}} {motors} because it is linked to a condition there. Remove the condition first.",
  },
  motorSelectorAvailable: {
    id: "propertySelector.available",
    defaultMessage: "Available ({count})",
  },
  motorSelectorSelected: {
    id: "propertySelector.selected",
    defaultMessage: "Selected ({count})",
  },
});

const AGR_SUPPORTED_LOCALES = ["es", "ca", "eu", "da", "de", "nl", "en", "fr", "hu", "it", "ja", "ko", "pt", "ro", "ru", "zh"];

const EMPTY_ARRAY = [];

const nestedFormWidgets = {
  configurations: asBookingEngineEntity(CancellationPolicyConfigurationForm, {
    entityName: CONSTANTS.ENTITY.CANCELLATIONPOLICYCONFIGURATION.name,
    type: "FORM",
  }),
};

const nestedListWidgets = {
  configurations: asBookingEngineEntity(CancellationPolicyConfigurationList, {
    entityName: CONSTANTS.ENTITY.CANCELLATIONPOLICYCONFIGURATION.name,
    type: "LIST",
  }),
};

function CancellationPolicyForm(props) {
  const {
    config,
    locales,
    entity,
    entityError,
    entityResult,
    entityName,
    loading,
    getListPath,
    entities,
    onSubmitEntity,
  } = props;

  const history = useHistory();
  const intl = useIntl();
  const propertyConfig = usePropertyConfiguration();
  const chainData = useChainData();
  const isChain = !!chainData?.isChain;
  const chainChildren = chainData?.children || EMPTY_ARRAY;
  const showEnableAutomaticCharges = propertyConfig?.enableAutomaticCharges === "1";

  const [agrSaving, setAgrSaving] = useState(false);
  const [agrSaveError, setAgrSaveError] = useState(null);
  const agrErrorRef = useRef(null);

  useEffect(() => {
    if (agrSaveError && agrErrorRef.current) {
      agrErrorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [agrSaveError]);

  // Fetch AGR data: childPropertyTickers (chain edit) or parentPropertyTicker (individual readonly)
  const [agrChildTickers, setAgrChildTickers] = useState(undefined);
  const [agrMotorConditionMap, setAgrMotorConditionMap] = useState({});
  const [localSelectedMotors, setLocalSelectedMotors] = useState([]);
  const [resolvedParentPropertyTicker, setResolvedParentPropertyTicker] = useState(null);

  useEffect(() => {
    if (agrChildTickers !== undefined) {
      const validMotors = agrChildTickers.filter(t => chainChildren.includes(t));
      setLocalSelectedMotors(validMotors);
    }
  }, [agrChildTickers, chainChildren]);

  useEffect(() => {
    const propertyTicker = entity?.propertyTicker || entity?.hotelTicker;
    if (!entity?.ticker || !propertyTicker) return;
    const baseURL = config.restConfig?.baseURL || "";

    if (isChain) {
      const controller = new AbortController();
      fetchAgrPolicyData(baseURL, propertyTicker, entity.ticker, controller.signal)
        .then(data => {
          const parentEntry = data[propertyTicker];
          setAgrChildTickers(
            Array.isArray(parentEntry?.childPropertyTickers) ? parentEntry.childPropertyTickers : []
          );
          const conditionMap = {};
          Object.keys(data).forEach(key => {
            if (key !== propertyTicker) {
              conditionMap[key] = data[key]?.linkedToCondition === true;
            }
          });
          setAgrMotorConditionMap(conditionMap);
        })
        .catch(err => {
          if (err.name !== "CanceledError" && err.name !== "AbortError") setAgrChildTickers([]);
        });
      return () => controller.abort();
    }

    if (isAgrPolicy(entity)) {
      if (entity.parentPropertyTicker) {
        setResolvedParentPropertyTicker(entity.parentPropertyTicker);
        return;
      }
      const controller = new AbortController();
      fetchAgrPolicyData(baseURL, propertyTicker, entity.ticker, controller.signal)
        .then(data => {
          const parentKey = Object.keys(data).find(key => key !== propertyTicker);
          if (parentKey) setResolvedParentPropertyTicker(parentKey);
        })
        .catch(err => {
          if (err.name !== "CanceledError" && err.name !== "AbortError") {
            console.warn("Could not resolve AGR parent property ticker", err);
          }
        });
      return () => controller.abort();
    }

    setAgrChildTickers(undefined);
    setResolvedParentPropertyTicker(null);
  }, [isChain, entity?.ticker, entity?.propertyTicker, entity?.hotelTicker,
      entity?.isLinkedToAggregator, entity?.parentPropertyTicker, config.restConfig?.baseURL]);

  const entityWithChildren = useMemo(() => {
    if (!isChain || agrChildTickers === undefined) return entity;
    return { ...entity, childPropertyTickers: agrChildTickers };
  }, [entity, isChain, agrChildTickers]);

  const isAgrLinkedInIndividual = useMemo(
    () => !isChain && isAgrPolicy(entityWithChildren || {}),
    [isChain, entityWithChildren]
  );

  const agrReadonlyLink = useMemo(() => {
    const parentPropertyTicker = entityWithChildren?.parentPropertyTicker || resolvedParentPropertyTicker;
    if (!isAgrLinkedInIndividual || !parentPropertyTicker) return null;
    const parentTicker = entityWithChildren.parentTicker || parentTickerOf(entityWithChildren.ticker || "");
    return buildAgrReadonlyLink(parentPropertyTicker, parentTicker, entityName);
  }, [isAgrLinkedInIndividual, entityWithChildren, resolvedParentPropertyTicker, entityName]);

  const { name, validation, fields, renderers, translation, locale } = config;

  const entityToFormMapper = useCallback(
    ({ entity, fields, locales, translationConfig }) => {
      const formValues = entityToFormValuesMapper({ entity, fields, locales, translationConfig });
      return {
        ...formValues,
        propertyTicker: entity.propertyTicker || entity.hotelTicker,
        showEnableAutomaticCharges,
        _isChain: !!isChain,
        _chainChildren: chainChildren,
        selectedMotors: Array.isArray(entity.childPropertyTickers)
          ? entity.childPropertyTickers
          : Array.isArray(entity.motorsList) ? entity.motorsList : [],
      };
    },
    [showEnableAutomaticCharges, isChain, chainChildren]
  );

  const mergedRenderers = useMemo(
    () => ({
      ...(renderers?.form || {}),
      agrWarningMessage: () => (
        <AgrWarning>{intl.formatMessage(messages.agrWarningMessage)}</AgrWarning>
      ),
      ...(isChain && {
        selectedMotors: () => {
          const availableCount = Math.max(0, chainChildren.length - localSelectedMotors.length);
          const field = {
            fieldName: "selectedMotors",
            options: chainChildren.map(ticker => ({ label: ticker, value: ticker })),
            leftListHeader: intl.formatMessage(messages.motorSelectorAvailable, { count: availableCount }),
            rightListHeader: intl.formatMessage(messages.motorSelectorSelected, { count: localSelectedMotors.length }),
          };
          return (
            <DualListBoxGroup
              value={localSelectedMotors}
              onChange={({ value }) => setLocalSelectedMotors(value)}
              field={field}
            />
          );
        },
      }),
    }),
    [renderers, intl, isChain, chainChildren, localSelectedMotors]
  );

  const customValidate = useCallback(
    formValues => {
      if ((formValues.validFrom && !formValues.validTo) || (formValues.validTo && !formValues.validFrom)) {
        return intl.formatMessage(messages.bothOrNone);
      }
      return "";
    },
    [intl]
  );

  const handleSubmit = useCallback(
    ({ formValues }) => {
      const { id, showEnableAutomaticCharges: _sc, _isChain: _ic, agrWarningMessage: _am,
              _chainChildren: _cc, selectedMotors, selectedMotorsSection: _sms, ...otherFormValues } = formValues;
      const entity = { ...otherFormValues };

      delete entity[undefined];
      if (entity.ordering !== undefined) entity.ordering = parseInt(entity.ordering, 10) || 0;
      entity.configurations = normalizeConfigurations(entity.configurations);

      if (id !== "" && id !== " " && id !== null && id !== undefined) {
        entity.id = id;
      }
      if (entity.validFrom === "" && entity.validTo === "") {
        entity.validFrom = undefined;
        entity.validTo = undefined;
      }

      if (isChain) {
        const newMotors = localSelectedMotors;
        if (newMotors.length === 0) {
          setAgrSaveError(intl.formatMessage(messages.agrMinOneMotorError));
          return;
        }
        const originalMotors = (agrChildTickers || []).filter(t => chainChildren.includes(t));
        const removedLinkedMotors = originalMotors.filter(
          t => !newMotors.includes(t) && agrMotorConditionMap[t] === true
        );
        if (removedLinkedMotors.length > 0) {
          setAgrSaveError(
            intl.formatMessage(messages.agrSaveErrorLinkedToConditionMotors, {
              motors: removedLinkedMotors.join(", "),
              count: removedLinkedMotors.length,
            })
          );
          return;
        }
        const propertyTicker = entity.propertyTicker;
        const { chainBody, parentTicker } = buildPolicyChainBody(entity, propertyTicker, newMotors);
        const baseURL = config.restConfig?.baseURL || "";
        const isEdit = id !== "" && id !== " " && id !== null && id !== undefined;

        setAgrSaving(true);
        saveAgrPolicy(baseURL, propertyTicker, parentTicker, chainBody, isEdit)
          .then(() => {
            setAgrSaveError(null);
            history.push(getListPath({ hotelTicker: propertyTicker, entityName }));
          })
          .catch(error => {
            console.error("Error saving AGR cancellation policy", error);
            const rawMessage = typeof error.response?.data === "string" ? error.response.data : "";
            const isLinkedToConditions = rawMessage.toLowerCase().includes("linked to conditions");
            setAgrSaveError(
              isLinkedToConditions
                ? intl.formatMessage(messages.agrSaveErrorLinkedToConditions)
                : intl.formatMessage(messages.agrSaveError)
            );
          })
          .finally(() => setAgrSaving(false));
      } else {
        onSubmitEntity({ entity });
      }
    },
    [isChain, intl, onSubmitEntity, config, entityName, getListPath, history, localSelectedMotors,
     agrChildTickers, agrMotorConditionMap, chainChildren]
  );

  const formFields = useMemo(() => {
    const base = fields && fields.form;
    if (!isAgrLinkedInIndividual || !base) return base;
    if (showEnableAutomaticCharges) {
      const editableFields = ["enableAutomaticCharge", "penaltyDeposit"];
      return base.map(f => ({ ...f, disabled: !editableFields.includes(f.fieldName) }));
    }
    return base.map(f => ({ ...f, disabled: true }));
  }, [fields, isAgrLinkedInIndividual, showEnableAutomaticCharges]);

  return (
    <div className={"wbkv9-form-cancellationPolicy"}>
      {isAgrLinkedInIndividual && (
        <AgrReadonlyBanner>
          {intl.formatMessage(messages.agrReadonlyMessage)}
          {" "}
          {intl.formatMessage(messages.agrReadonlyEditRest)}{" "}
          {agrReadonlyLink ? (
            <a href={agrReadonlyLink}>{intl.formatMessage(messages.agrReadonlyLink)}</a>
          ) : (
            intl.formatMessage(messages.agrReadonlyLink)
          )}
        </AgrReadonlyBanner>
      )}
      <EntityForm
        loading={loading || agrSaving}
        name={name}
        entity={entityWithChildren}
        entities={entities}
        validation={validation}
        renderers={mergedRenderers}
        fields={formFields}
        locales={isChain ? AGR_SUPPORTED_LOCALES : locales}
        locale={locale}
        translation={translation}
        onSubmit={handleSubmit}
        customValidate={customValidate}
        entityError={entityError}
        entityResult={entityResult}
        entityName={entityName}
        getListPath={getListPath}
        nestedFormWidgets={nestedFormWidgets}
        nestedListWidgets={nestedListWidgets}
        entityToFormValuesMapper={entityToFormMapper}
        readOnly={isAgrLinkedInIndividual && !showEnableAutomaticCharges}
      />
      {agrSaveError && (
        <AgrError ref={agrErrorRef}>{agrSaveError}</AgrError>
      )}
    </div>
  );
}


CancellationPolicyForm.defaultProps = {
  locales: ["es", "ca", "en", "fr", "it", "de"],
};

export default CancellationPolicyForm;
